import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import { FaFileExcel, FaFilePdf } from 'react-icons/fa';
import { MdUpload, MdDelete } from 'react-icons/md';
import { Button } from 'react-bootstrap';
import { ClipLoader } from 'react-spinners';
import { post } from "../../api/usersModule/httpClient";
import Swal from 'sweetalert2';

const Container = styled.div`
  border: 2px dashed #aaa;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
`;

const Message = styled.p`
  font-size: 16px;
  margin: 0;
`;

const FileContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  width: 100%;
`;

const FileName = styled.p`
  margin: 0 10px;
  text-align: left;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ExcelIcon = styled(FaFileExcel)`
  font-size: 48px;
  color: #217346;
`;

const PDFIcon = styled(FaFilePdf)`
  font-size: 48px;
  color: #D91212;
`;

const Dropzone = ({ project, setPressedButton, userName, setRefresh, setSelectedPDF, purchaseState, BillLink, selectedButton }) => {
  const [selectedFile, setSelectedFile] = useState(BillLink || null);
  const [fileBase64, setFileBase64] = useState(null);
  const [loading, setLoading] = useState(false);

  if (purchaseState === undefined) {
    purchaseState = false
  }

  const onDrop = useCallback((acceptedFiles) => {
    if (purchaseState) {
      // Handle PDF file reading (unchanged)
      const file = acceptedFiles[0];
      const fileType = file.type;
      const validFileTypes = ['application/pdf'];

      if (validFileTypes.includes(fileType)) {
        setSelectedFile(file);

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          setSelectedPDF(String(reader.result));
        };
        reader.onerror = (error) => {
          console.log(error);
        };
      } else {
        alert('Por favor, selecciona un archivo PDF válido');
      }
    } else {
      // Handle Excel file reading
      const file = acceptedFiles[0];
      const fileType = file.type;
      const validFileTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel.sheet.macroEnabled.12',
      ];

      if (validFileTypes.includes(fileType)) {
        setSelectedFile(file);

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          setFileBase64(String(reader.result));
        };
        reader.onerror = (error) => {
          console.log(error);
        };
      } else {
        alert('Por favor, selecciona un archivo Excel válido');
      }
    }
  }, [setSelectedPDF]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleDelete = () => {
    if (setSelectedPDF && typeof setSelectedPDF === 'function') {
      setSelectedPDF('');
    }
    setSelectedFile(null);
    setFileBase64(null);
  };

  const handleUpload = async () => {
    try {
      // Deshabilitar el botón de eliminar durante la carga
      const deleteButton = document.getElementById('deleteButton');
      if (deleteButton) {
        deleteButton.disabled = true;
      }

      const result = await Swal.fire({
        title: '¡Atención!',
        text: 'En el caso de detectar un SKU ya existente con el mismo código SAP en la base de datos, los datos serán actualizados con la información provista en el archivo. ¿Desea actualizar?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Actualizar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#146284',
        cancelButtonColor: '#EF4444',
      });

      if (!result.isConfirmed) {
        // Habilitar el botón de eliminar si se cancela la carga
        if (deleteButton) {
          deleteButton.disabled = false;
        }
        return;
      }

      setLoading(true);

      const body = {
        projectID: project,
        User: userName || 'Admin',
        uploadType: selectedButton,
      };

      try {
        const response = await post('/skus', {
          ...body,
          excelFile: fileBase64.split(',')[1],
        }, 'El archivo ha sido cargado con éxito. Pronto recibirás una notificación una vez que los datos hayan sido procesados y validados.');

        if(response.answer) {
          setPressedButton("closeModal")
        }else {
          Swal.fire({
            icon: 'error',
            title: 'Error al subir el archivo',
            text: 'Por favor, valida el formato y la información del archivo.',
            confirmButtonColor: '#146284',
            confirmButtonText: 'Entendido'
          });        
        }
      } catch (error) {
        console.log(error);
        showError();
      } finally {
        setLoading(false);

        // Habilitar el botón de eliminar después de la carga
        if (deleteButton) {
          deleteButton.disabled = false;
        }
      }
    } catch {
      alert('Por favor, selecciona un archivo Excel válido');
    }
  };

  const showError = () => {
    Swal.fire({
      title: 'Error!',
      text: 'Ocurrió un error al insertar los SKUS, por favor verifica tu archivo e inténtalo de nuevo',
      icon: 'error',
      confirmButtonColor: '#146284',
    });
  };

  return (
    <div>
      {selectedFile ? (
        <FileContainer>
          <InfoContainer>
            {(purchaseState === undefined || purchaseState === false || purchaseState == "") && (
              <ExcelIcon />
            )}
            {purchaseState && (
              <PDFIcon />
            )}
            <FileName>{selectedFile?.name || selectedFile || BillLink}</FileName>
          </InfoContainer>
          <ButtonContainer>
            {purchaseState == undefined || purchaseState == false && (
              <Button variant='outline-success' onClick={handleUpload} disabled={loading}>
                {loading ? (
                  <div className='d-flex align-items-center gap-1'>
                    <ClipLoader size={20} color='#198754' />
                    Cargando...
                  </div>
                ) : (
                  <>
                    <MdUpload />
                    Cargar
                  </>
                )}
              </Button>
            )}
            <Button id='deleteButton' variant='outline-danger' onClick={handleDelete} disabled={loading}>
              <MdDelete />
              Eliminar
            </Button>
          </ButtonContainer>
        </FileContainer>
      ) : (
        <Container {...getRootProps()}>
          <input {...getInputProps()} />
          {isDragActive ? (
            <Message>Suelta el archivo aquí...</Message>
          ) : (
            <Message>
              {purchaseState && (
                'Arrastra y suelta un archivo PDF aquí, o haz clic para seleccionar uno'
              )}
              {(purchaseState === undefined || purchaseState === false || purchaseState == "") && (
                'Arrastra y suelta un archivo Excel aquí, o haz clic para seleccionar uno'
              )}
            </Message>
          )}
        </Container>
      )}
    </div>
  );
};

export default Dropzone;